import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useCurrency } from '../context/CurrencyContext';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Coins, 
  Zap, 
  TrendingUp, 
  Trophy,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  RotateCw,
  Check,
  X,
  Sparkles,
  Droplets,
  Shield,
  Wallet,
  Plus,
  Minus,
  ArrowDownToLine,
  ArrowUpFromLine,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// High-quality coin images
const HEADS_IMAGE = "https://customer-assets.emergentagent.com/job_0da9667c-077b-4722-a58b-eb4246cf4502/artifacts/sejengph_0A4E0961-C4E5-4F17-94CD-8890F49281A0.png";
const TAILS_IMAGE = "https://customer-assets.emergentagent.com/job_0da9667c-077b-4722-a58b-eb4246cf4502/artifacts/wfchuxa5_IMG_4291.png";

// STXOSHI Contract
const STXOSHI_CONTRACT = "SP1Z92MPDQEWZXW36VX71Q25HKF5K2EPCJ304F275.tokensoft-token-v4karmaedcr";

// Celebratory messages
const WIN_MESSAGES = [
  { text: "CORRECT! 1 down!", emoji: "🎯" },
  { text: "2 in a row! Keep it up!", emoji: "🔥" },
  { text: "HAT TRICK! 3 correct!", emoji: "💪" },
  { text: "FOUR STRAIGHT!", emoji: "⭐" },
  { text: "5 IN A ROW!", emoji: "🚀" },
  { text: "SIX! LEGENDARY!", emoji: "👑" },
  { text: "SEVEN! WIZARD!", emoji: "⚡" },
  { text: "8 STRAIGHT! INSANE!", emoji: "🎆" },
  { text: "NINE! ONE MORE!", emoji: "🙏" },
  { text: "PERFECT 10!!!", emoji: "🎰" }
];

const FINAL_WIN_MESSAGES = [
  { text: "YOU WON!", emoji: "🎉" },
  { text: "DOUBLE WIN!", emoji: "🎊🎉" },
  { text: "TRIPLE THREAT!", emoji: "🏆🎉🎊" },
  { text: "QUAD MASTER!", emoji: "👑🎉🎊🏆" },
  { text: "PENTA KILL!", emoji: "🔥👑🎉🎊🏆" },
  { text: "LEGENDARY!", emoji: "⭐🔥👑🎉🎊🏆" },
  { text: "GODLIKE!", emoji: "💎⭐🔥👑🎉🎊🏆" },
  { text: "UNSTOPPABLE!", emoji: "🚀💎⭐🔥👑🎉🎊🏆" },
  { text: "IMPOSSIBLE!", emoji: "🌟🚀💎⭐🔥👑🎉🎊🏆" },
  { text: "JACKPOT!!!", emoji: "💰🌟🚀💎⭐🔥👑🎉🎊🏆" }
];

const LOSE_MESSAGES = [
  { text: "Missed on flip 1...", emoji: "😢" },
  { text: "So close! Missed flip 2", emoji: "😔" },
  { text: "Streak broken at flip 3", emoji: "💔" },
  { text: "4 correct then... nope", emoji: "😭" },
  { text: "5 wins then heartbreak", emoji: "💫" },
  { text: "6 correct, then defeat", emoji: "🥺" },
  { text: "7 correct! So close!", emoji: "😞" },
  { text: "8 wins wasted... brutal", emoji: "💀" },
  { text: "9 CORRECT and missed?!", emoji: "🫠" },
  { text: "The final flip... NO!", emoji: "😵" }
];

// Confetti component
function Confetti({ show, intensity = 'normal' }) {
  if (!show) return null;
  
  const colors = ['#ff6b00', '#ffd700', '#00ff88', '#00bfff', '#ff69b4', '#9400d3', '#ff4444', '#44ff44'];
  const count = intensity === 'epic' ? 150 : intensity === 'big' ? 80 : 50;
  
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {Array.from({ length: count }).map((_, i) => {
        const isGold = Math.random() > 0.6;
        return (
          <div
            key={i}
            className="absolute animate-confetti"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-20px',
              width: isGold ? '12px' : `${6 + Math.random() * 8}px`,
              height: isGold ? '12px' : `${6 + Math.random() * 8}px`,
              backgroundColor: isGold ? '#ffd700' : colors[Math.floor(Math.random() * colors.length)],
              borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              animationDelay: `${Math.random() * 0.8}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
              boxShadow: isGold ? '0 0 8px #ffd700' : 'none'
            }}
          />
        );
      })}
    </div>
  );
}

// 3D Coin Component
function FlipCoin3D({ isSpinning, finalResult, onAnimationEnd, initialFace }) {
  const coinRef = useRef(null);
  const [rotation, setRotation] = useState({ x: 0, y: initialFace === 'tails' ? 180 : 0 });
  const [showResult, setShowResult] = useState(false);
  const animationRef = useRef(null);
  
  // Update initial rotation when prediction changes (before spinning)
  useEffect(() => {
    if (!isSpinning && !finalResult) {
      setRotation({ x: 0, y: initialFace === 'tails' ? 180 : 0 });
    }
  }, [initialFace, isSpinning, finalResult]);
  
  useEffect(() => {
    if (isSpinning && finalResult) {
      setShowResult(false);
      
      let currentRotation = rotation.y; // Start from current position
      let velocity = 45;
      const friction = 0.985;
      const minVelocity = 0.5;
      
      const targetFace = finalResult === 'heads' ? 0 : 180;
      const fullSpins = 6 + Math.floor(Math.random() * 3);
      const targetRotation = fullSpins * 360 + targetFace;
      
      const animate = () => {
        currentRotation += velocity;
        velocity *= friction;
        
        const wobble = Math.sin(currentRotation * 0.1) * (velocity * 0.3);
        
        setRotation({ x: wobble, y: currentRotation });
        
        if (velocity > minVelocity && currentRotation < targetRotation) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setRotation({ x: 0, y: targetFace });
          setShowResult(true);
          setTimeout(() => onAnimationEnd?.(), 500);
        }
      };
      
      animationRef.current = requestAnimationFrame(animate);
      
      return () => {
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
      };
    }
  }, [isSpinning, finalResult, onAnimationEnd]);
  
  return (
    <div className="relative w-40 h-40 mx-auto" style={{ perspective: '1000px' }}>
      <div className={`absolute inset-0 rounded-full transition-all duration-300 ${
        isSpinning ? 'bg-orange-500/40 blur-2xl scale-125 animate-pulse' : 
        showResult ? (finalResult === 'heads' ? 'bg-orange-500/30' : 'bg-cyan-500/30') + ' blur-xl' : 
        'bg-orange-500/20 blur-lg'
      }`} />
      
      <div
        ref={coinRef}
        className="relative w-full h-full"
        style={{
          transformStyle: 'preserve-3d',
          transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
          transition: isSpinning ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        <div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(0deg)',
            boxShadow: '0 0 0 4px rgba(255, 140, 0, 0.5), 0 10px 40px rgba(0, 0, 0, 0.5)'
          }}
        >
          <img src={HEADS_IMAGE} alt="Heads" className="w-full h-full object-cover" style={{ imageRendering: 'crisp-edges' }} />
          <div className="absolute inset-0 rounded-full pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(0,0,0,0.2) 100%)' }} />
        </div>
        
        <div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            boxShadow: '0 0 0 4px rgba(0, 191, 255, 0.5), 0 10px 40px rgba(0, 0, 0, 0.5)'
          }}
        >
          <img src={TAILS_IMAGE} alt="Tails" className="w-full h-full object-cover" style={{ imageRendering: 'crisp-edges' }} />
          <div className="absolute inset-0 rounded-full pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(0,0,0,0.2) 100%)' }} />
        </div>
      </div>
      
      {isSpinning && (
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="absolute w-2 h-2 bg-yellow-300 rounded-full animate-ping"
              style={{ top: `${20 + Math.random() * 60}%`, left: `${20 + Math.random() * 60}%`, animationDelay: `${i * 0.1}s` }} />
          ))}
        </div>
      )}
    </div>
  );
}

// Prediction Card for each flip
function PredictionCard({ flipNumber, prediction, onSelect, isLocked, isComplete, result, wasCorrect }) {
  if (isComplete) {
    return (
      <div className={`p-2 rounded-lg border flex items-center gap-3 ${wasCorrect ? 'border-green-500 bg-green-500/10' : 'border-red-500 bg-red-500/10'}`}>
        <span className="font-bold text-sm w-12">#{flipNumber}</span>
        <img src={prediction === 'heads' ? HEADS_IMAGE : TAILS_IMAGE} alt={prediction} className="w-8 h-8 rounded-full" />
        <span className="text-xs uppercase">{prediction}</span>
        <span className="text-xs text-muted-foreground">→</span>
        <span className={`text-xs font-bold uppercase ${wasCorrect ? 'text-green-400' : 'text-red-400'}`}>
          {result} {wasCorrect ? '✓' : '✗'}
        </span>
      </div>
    );
  }
  
  return (
    <div className={`p-1.5 rounded-lg border transition-all ${isLocked ? 'border-green-500/50 bg-green-500/5' : 'border-orange-500/30 bg-orange-500/5'}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold w-6">#{flipNumber}</span>
        <button
          onClick={() => !isLocked && onSelect('heads')}
          disabled={isLocked}
          className={`flex-1 py-1.5 px-2 rounded flex items-center justify-center gap-1.5 transition-all
            ${prediction === 'heads'
              ? 'bg-orange-500/30 border border-orange-500'
              : 'bg-muted/30 border border-transparent hover:border-orange-500/50'
            }
            ${isLocked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <img src={HEADS_IMAGE} alt="H" className="w-5 h-5 rounded-full" />
          <span className="text-xs font-bold">Heads</span>
        </button>
        <button
          onClick={() => !isLocked && onSelect('tails')}
          disabled={isLocked}
          className={`flex-1 py-1.5 px-2 rounded flex items-center justify-center gap-1.5 transition-all
            ${prediction === 'tails'
              ? 'bg-cyan-500/30 border border-cyan-500'
              : 'bg-muted/30 border border-transparent hover:border-cyan-500/50'
            }
            ${isLocked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <img src={TAILS_IMAGE} alt="T" className="w-5 h-5 rounded-full" />
          <span className="text-xs font-bold">Tails</span>
        </button>
        {prediction && <Check className="w-4 h-4 text-green-400 flex-shrink-0" />}
      </div>
    </div>
  );
}

// Player Wallet Card - Compact version
function PlayerWalletCard({ balance, onDeposit, onWithdraw, loading, inPlay = 0, flash = null }) {
  const { showUsd, stxoshiUsd } = useCurrency();
  const balanceStxoshi = (balance || 0) / 1_000_000;
  const isPositive = balance > 0;
  const balDisplay = showUsd
    ? `$${(balanceStxoshi * (stxoshiUsd || 0.0000037)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : balanceStxoshi.toLocaleString();
  const flashClass = flash === 'up' ? 'wallet-flash-up' : flash === 'down' ? 'wallet-flash-down' : '';
  
  return (
    <Card className={`glass-card ${isPositive ? 'border-green-500/50' : 'border-orange-500/50'}`}>
      <CardContent className="p-2 sm:p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <Wallet className="w-4 h-4 text-orange-400 flex-shrink-0" />
            <span
              key={`${balance}-${flash}`}
              data-testid="flip-wallet-balance"
              className={`text-base sm:text-lg font-mono font-bold truncate ${isPositive ? 'text-green-400' : 'text-foreground'} ${flashClass}`}
            >
              {balDisplay}
            </span>
            <span className="text-[10px] text-muted-foreground whitespace-nowrap">{showUsd ? "USD" : "User Wallet"}</span>
          </div>
          <div className="flex gap-1.5 flex-shrink-0">
            <Button size="sm" onClick={onDeposit} disabled={loading} className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700">
              <Plus className="w-3 h-3 mr-0.5" /> Deposit
            </Button>
            <Button size="sm" onClick={onWithdraw} disabled={loading || balance <= 0} variant="outline"
              className="h-7 px-2 text-xs border-orange-500 text-orange-400 hover:bg-orange-500/10">
              <Minus className="w-3 h-3 mr-0.5" /> Withdraw
            </Button>
          </div>
        </div>
        {inPlay > 0 && (
          <div
            data-testid="flip-in-play"
            className="mt-1.5 flex items-center gap-1.5 rounded-md border border-orange-500/40 bg-orange-500/10 px-2 py-1 animate-inplay"
          >
            <Zap className="w-3 h-3 text-orange-400 flex-shrink-0" />
            <span className="text-[11px] font-mono font-semibold text-orange-300">
              {(inPlay / 1_000_000).toLocaleString()} STXOSHI in play
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Slot selector
function FlipSlotSelector({ value, onChange, disabled }) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Number of Flips</span>
        <span className="font-mono font-bold text-primary">{value}x</span>
      </div>
      <div className="grid grid-cols-10 gap-1">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((slot) => (
          <button
            key={slot}
            onClick={() => !disabled && onChange(slot)}
            disabled={disabled}
            className={`p-2 rounded-lg text-sm font-mono transition-all
              ${value === slot ? 'bg-primary text-primary-foreground shadow-[0_0_15px_rgba(249,115,22,0.5)]' : 'bg-muted/50 hover:bg-muted'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            {slot}
          </button>
        ))}
      </div>
    </div>
  );
}

// Payout display - shows TOTAL stake (per flip × num flips) and NET PROFIT
function PayoutDisplay({ stakePerFlip, numFlips, price }) {
  const totalStake = stakePerFlip * numFlips;  // Total = per flip × number of flips
  const multiplier = Math.pow(2, numFlips);
  const grossPayout = totalStake * multiplier * 0.97;  // What you get back if you win
  const netProfit = grossPayout - totalStake;  // Actual profit (what you gain)
  const stxoshiStake = totalStake / 1_000_000;
  const stxoshiPerFlip = stakePerFlip / 1_000_000;
  const stxoshiProfit = netProfit / 1_000_000;
  
  return (
    <div className="space-y-2 p-3 rounded-xl bg-muted/30 border border-border text-sm">
      <div className="flex justify-between">
        <span className="text-muted-foreground">Per Flip</span>
        <span className="font-mono">{stxoshiPerFlip.toLocaleString()} STXOSHI</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">× {numFlips} Flips</span>
        <span className="font-mono font-bold">{stxoshiStake.toLocaleString()} STXOSHI</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Multiplier</span>
        <Badge variant="outline" className="text-primary border-primary">{multiplier}x</Badge>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">Win Chance</span>
        <span className="font-mono">{((1 / multiplier) * 100).toFixed(2)}%</span>
      </div>
      <div className="h-px bg-border" />
      <div className="flex justify-between">
        <span className="font-medium">Net Profit if Win</span>
        <span className="font-mono font-bold text-green-400">+{stxoshiProfit.toLocaleString()} STXOSHI</span>
      </div>
    </div>
  );
}

// Main Page Component
export default function FlipMarket() {
  const navigate = useNavigate();
  const { isConnected, connectWallet, getAuthHeaders, addresses } = useWallet();
  
  // Player Wallet state
  const [playerBalance, setPlayerBalance] = useState(0);
  const [depositAmount, setDepositAmount] = useState('');
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [pendingDeposit, setPendingDeposit] = useState(null); // {deposit_id, tx_id, amount}
  const [checkingOnChain, setCheckingOnChain] = useState(false);
  
  // Game setup state
  const [step, setStep] = useState('setup'); // setup, predictions, playing, result
  const [numFlips, setNumFlips] = useState(2);
  const [stakePerFlip, setStakePerFlip] = useState(10_000_000);
  const [customStake, setCustomStake] = useState('');
  
  // Predictions state - array of predictions for each flip
  const [predictions, setPredictions] = useState([]);
  
  // Game play state
  const [currentFlipIndex, setCurrentFlipIndex] = useState(0);
  const [flipResults, setFlipResults] = useState([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [gameState, setGameState] = useState('ready');
  const [lastFlipResult, setLastFlipResult] = useState(null);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiIntensity, setConfettiIntensity] = useState('normal');
  // Suspense-preserving reveal: keep the wallet number frozen while flipping,
  // show the wagered amount as "in play", and only update (with a flash) AFTER
  // the reveal completes.
  const [stakeInPlay, setStakeInPlay] = useState(0);
  const [walletFlash, setWalletFlash] = useState(null); // 'up' | 'down' | null
  
  // Data state
  const [price, setPrice] = useState(null);
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [betHistory, setBetHistory] = useState([]);
  
  // Initialize predictions array when numFlips changes
  useEffect(() => {
    setPredictions(Array(numFlips).fill(null));
  }, [numFlips]);
  
  // Fetch functions
  const fetchPlayerWallet = async () => {
    if (!isConnected) return;
    try {
      const response = await fetch(`${API}/api/flip-market/player-wallet`, { headers: getAuthHeaders() });
      const data = await response.json();
      if (data.success) setPlayerBalance(data.data.balance_stxoshi);
    } catch (error) {
      console.error('Failed to fetch player wallet:', error);
    }
  };
  
  const fetchPrice = async () => {
    try {
      const response = await fetch(`${API}/api/flip-market/price`);
      const data = await response.json();
      if (data.success) setPrice(data.data.price_usd);
    } catch (error) {
      console.error('Failed to fetch price:', error);
    }
  };
  
  const fetchPools = async () => {
    try {
      const response = await fetch(`${API}/api/flip-market/pools`);
      const data = await response.json();
      if (data.success) setPools(data.data);
    } catch (error) {
      console.error('Failed to fetch pools:', error);
    }
  };
  
  const fetchBetHistory = async () => {
    try {
      const response = await fetch(`${API}/api/flip-market/my-bets`, { headers: getAuthHeaders() });
      const data = await response.json();
      if (data.success) setBetHistory(data.data);
    } catch (error) {
      console.error('Failed to fetch bet history:', error);
    }
  };
  
  useEffect(() => {
    fetchPrice();
    fetchPools();
    if (isConnected) {
      fetchPlayerWallet();
      fetchBetHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);
  
  // Handle deposit using sats-connect (same as LP deposits)
  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount) * 1_000_000;
    if (!amount || amount < 1_000_000) {
      toast.error('Minimum deposit is 1 STXOSHI');
      return;
    }
    
    setLoading(true);
    try {
      // Step 1: Initiate deposit
      const initResponse = await fetch(`${API}/api/flip-market/player-wallet/initiate-deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ amount_stxoshi: amount })
      });
      
      const initData = await initResponse.json();
      if (!initData.success) throw new Error(initData.detail || 'Failed to initiate deposit');
      
      const { deposit_id, recipient_address } = initData;
      
      // Step 2: Sign STXOSHI transfer using sats-connect (same as LP deposits)
      toast.info('Please sign the STXOSHI transfer in your wallet...');
      
      const satsConnect = await import('sats-connect');
      const stacksTx = await import('@stacks/transactions');
      
      // Build post-condition
      const postCondition = stacksTx.Pc.principal(addresses.stx)
        .willSendEq(BigInt(amount))
        .ft(STXOSHI_CONTRACT, 'tokensoft-token');
      
      const payload = {
        contract: STXOSHI_CONTRACT,
        functionName: 'transfer',
        arguments: [
          stacksTx.cvToHex(stacksTx.uintCV(amount)),
          stacksTx.cvToHex(stacksTx.principalCV(addresses.stx)),
          stacksTx.cvToHex(stacksTx.principalCV(recipient_address)),
          stacksTx.cvToHex(stacksTx.noneCV())
        ],
        postConditions: [stacksTx.postConditionToHex(postCondition)],
        postConditionMode: 'deny'
      };
      
      const response = await satsConnect.request('stx_callContract', payload);
      
      if (!response?.result?.txid) {
        throw new Error('Transaction cancelled or failed');
      }
      
      const txId = response.result.txid;
      
      // Store pending deposit info in case confirm fails
      setPendingDeposit({ deposit_id, tx_id: txId, amount });
      
      // Step 3: Confirm deposit
      const confirmResponse = await fetch(`${API}/api/flip-market/player-wallet/confirm-deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ deposit_id, tx_id: txId })
      });
      
      const confirmData = await confirmResponse.json();
      if (confirmData.success) {
        setPlayerBalance(confirmData.data.new_balance);
        setShowDepositModal(false);
        setDepositAmount('');
        setPendingDeposit(null); // Clear pending on success
        toast.success(`Deposited ${amount / 1_000_000} STXOSHI to your User Wallet!`);
      } else {
        // Confirmation failed but tx was signed - keep pending deposit for manual check
        toast.error('Deposit signed but confirmation failed. Use "Check On-Chain" button.');
      }
    } catch (error) {
      console.error('Deposit error:', error);
      toast.error(error.message || 'Deposit failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Manual on-chain verification for stuck deposits
  const handleCheckOnChain = async () => {
    if (!pendingDeposit) return;
    
    setCheckingOnChain(true);
    try {
      // Check transaction status on-chain
      const txStatusResponse = await fetch(`https://api.hiro.so/extended/v1/tx/${pendingDeposit.tx_id}`);
      const txStatus = await txStatusResponse.json();
      
      if (txStatus.tx_status === 'success') {
        // Transaction succeeded on chain - try to confirm again
        const confirmResponse = await fetch(`${API}/api/flip-market/player-wallet/confirm-deposit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ 
            deposit_id: pendingDeposit.deposit_id, 
            tx_id: pendingDeposit.tx_id 
          })
        });
        
        const confirmData = await confirmResponse.json();
        if (confirmData.success) {
          setPlayerBalance(confirmData.data.new_balance);
          setShowDepositModal(false);
          setDepositAmount('');
          setPendingDeposit(null);
          toast.success(`Deposit verified! ${pendingDeposit.amount / 1_000_000} STXOSHI credited!`);
        } else {
          // Still failed - offer explorer link
          toast.info(
            <div>
              TX confirmed on-chain but not credited. 
              <a href={`https://explorer.hiro.so/txid/${pendingDeposit.tx_id}?chain=mainnet`} 
                 target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline ml-1">
                View TX
              </a>
            </div>
          );
        }
      } else if (txStatus.tx_status === 'pending') {
        toast.info('Transaction still pending on blockchain. Please wait a few minutes.');
      } else {
        toast.error(`Transaction ${txStatus.tx_status}. No funds were deducted.`);
        setPendingDeposit(null);
      }
    } catch (error) {
      console.error('On-chain check error:', error);
      toast.error('Failed to check on-chain status. Try again.');
    } finally {
      setCheckingOnChain(false);
    }
  };
  
  // Handle withdrawal
  const handleWithdraw = async () => {
    if (playerBalance <= 0) {
      toast.error('Nothing to withdraw');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/flip-market/player-wallet/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({})
      });
      
      const data = await response.json();
      if (data.success) {
        setPlayerBalance(data.data.new_balance);
        setShowWithdrawModal(false);
        toast.success(`Withdrew ${data.data.withdrawn_amount / 1_000_000} STXOSHI!`);
      } else {
        throw new Error(data.detail || 'Failed to withdraw');
      }
    } catch (error) {
      toast.error(error.message || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle prediction selection
  const handlePredictionSelect = (flipIndex, prediction) => {
    const newPredictions = [...predictions];
    newPredictions[flipIndex] = prediction;
    setPredictions(newPredictions);
  };
  
  // Check if all predictions are made
  const allPredictionsMade = predictions.every(p => p !== null);
  
  // Proceed to predictions step
  const handleProceedToPredictions = () => {
    const totalStakeNeeded = stakePerFlip * numFlips;
    if (totalStakeNeeded > playerBalance) {
      toast.error(`Insufficient balance! You need ${totalStakeNeeded / 1_000_000} STXOSHI (${stakePerFlip / 1_000_000} × ${numFlips} flips) but have ${playerBalance / 1_000_000}.`);
      setShowDepositModal(true);
      return;
    }
    setStep('predictions');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Start the game - just transition to playing phase, NO API call yet
  const handleStartGame = () => {
    if (!allPredictionsMade) {
      toast.error('Please make all your predictions first!');
      return;
    }
    
    // Just transition to playing state - don't call API yet
    // Stake will be deducted when user clicks FLIP COIN 1
    setFlipResults([]);
    setCurrentFlipIndex(0);
    setLastFlipResult(null);
    setStep('playing');
    setGameState('waiting');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // User clicks flip button - FIRST flip calls API and deducts stake
  const handleUserFlip = async () => {
    if (gameState !== 'waiting') return;
    
    // If this is the FIRST flip, call the API now
    if (currentFlipIndex === 0 && !lastFlipResult) {
      setLoading(true);
      try {
        const response = await fetch(`${API}/api/flip-market/instant-flip`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            stake_stxoshi: stakePerFlip,
            num_flips: numFlips,
            predictions: predictions
          })
        });
        
        const data = await response.json();
        
        if (data.success) {
          // DON'T deduct the displayed balance yet — keep it frozen through the
          // flip animation to preserve suspense. Show the wagered amount as
          // "in play" instead; the wallet updates (with a flash) only AFTER the
          // reveal. Total stake = stakePerFlip × numFlips.
          const totalStake = stakePerFlip * numFlips;
          setStakeInPlay(totalStake);
          setWalletFlash(null);
          
          // Store full result data (including final new_balance for after result)
          setLastFlipResult(data.data);
          // Now start the spin animation
          setGameState('spinning');
          setIsSpinning(true);
        } else {
          toast.error(data.detail || 'Failed to flip');
          setStep('predictions'); // Go back to predictions
        }
      } catch (error) {
        toast.error('Failed to flip');
        setStep('predictions');
      } finally {
        setLoading(false);
      }
    } else {
      // Subsequent flips - just animate using stored results
      setGameState('spinning');
      setIsSpinning(true);
    }
  };
  
  // When coin animation ends
  const settleBalance = useCallback((newBal) => {
    setPlayerBalance((prev) => {
      setWalletFlash(newBal >= prev ? 'up' : 'down');
      return newBal;
    });
    setStakeInPlay(0);
    setTimeout(() => setWalletFlash(null), 1300);
  }, []);

  const handleFlipAnimationEnd = useCallback(() => {
    if (!lastFlipResult) return;
    
    const result = lastFlipResult.results?.[currentFlipIndex];
    if (!result) return;
    
    // Compare against THIS flip's prediction
    const thisPrediction = predictions[currentFlipIndex];
    const isCorrect = result === thisPrediction;
    
    const newResults = [...flipResults, result];
    setFlipResults(newResults);
    setIsSpinning(false);
    
    if (isCorrect) {
      setShowConfetti(true);
      setConfettiIntensity('normal');
      setTimeout(() => setShowConfetti(false), 1500);
      
      if (newResults.length === numFlips) {
        // All flips done - use backend's won result
        setTimeout(() => {
          if (lastFlipResult.won) {
            setGameState('won');
            setCurrentMessage(FINAL_WIN_MESSAGES[numFlips - 1] || FINAL_WIN_MESSAGES[9]);
            setConfettiIntensity(numFlips >= 7 ? 'epic' : numFlips >= 4 ? 'big' : 'normal');
            setShowConfetti(true);
            setTimeout(() => {
              setShowConfetti(false);
              // NOW update the player balance after showing win result (flash up)
              if (lastFlipResult.new_balance !== undefined) {
                settleBalance(lastFlipResult.new_balance);
              }
              setStep('result');
              fetchPools(); // Refresh LP slot liquidity
            }, 4000);
          } else {
            // Backend says lost even though local check passed
            setGameState('lost');
            setCurrentMessage(LOSE_MESSAGES[newResults.length - 1] || LOSE_MESSAGES[0]);
            setTimeout(() => {
              // Update balance after showing loss (flash down)
              if (lastFlipResult.new_balance !== undefined) {
                settleBalance(lastFlipResult.new_balance);
              }
              setStep('result');
              fetchPools(); // Refresh LP slot liquidity
            }, 2000);
          }
        }, 1000);
      } else {
        setCurrentMessage(WIN_MESSAGES[newResults.length - 1]);
        setTimeout(() => {
          setCurrentFlipIndex(newResults.length);
          setGameState('waiting');
        }, 1500);
      }
    } else {
      setGameState('lost');
      setCurrentMessage(LOSE_MESSAGES[newResults.length - 1] || LOSE_MESSAGES[0]);
      setTimeout(() => {
        // Update balance after showing loss on wrong flip (flash down)
        if (lastFlipResult?.new_balance !== undefined) {
          settleBalance(lastFlipResult.new_balance);
        }
        setStep('result');
        fetchPools(); // Refresh LP slot liquidity
      }, 2000);
    }
  }, [lastFlipResult, currentFlipIndex, flipResults, numFlips, predictions, settleBalance]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Reset game
  const handlePlayAgain = () => {
    setStep('setup');
    setGameState('ready');
    setFlipResults([]);
    setCurrentFlipIndex(0);
    setCurrentMessage(null);
    setLastFlipResult(null);
    setStakeInPlay(0);
    setWalletFlash(null);
    setPredictions(Array(numFlips).fill(null));
    fetchPlayerWallet();
    fetchBetHistory();
  };
  
  return (
    <div className="min-h-screen bg-background">
      <style>{`
        @keyframes confetti {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .animate-confetti { animation: confetti 3s ease-out forwards; }
        @keyframes wallet-flash-up {
          0% { color: rgb(34,197,94); text-shadow: 0 0 18px rgba(34,197,94,0.9); transform: scale(1.12); }
          100% { color: inherit; text-shadow: none; transform: scale(1); }
        }
        @keyframes wallet-flash-down {
          0% { color: rgb(248,113,113); text-shadow: 0 0 18px rgba(248,113,113,0.9); transform: scale(1.12); }
          100% { color: inherit; text-shadow: none; transform: scale(1); }
        }
        .wallet-flash-up { animation: wallet-flash-up 1.3s ease-out; }
        .wallet-flash-down { animation: wallet-flash-down 1.3s ease-out; }
        @keyframes inplay-pulse {
          0%, 100% { opacity: 0.75; }
          50% { opacity: 1; }
        }
        .animate-inplay { animation: inplay-pulse 1.2s ease-in-out infinite; }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(249, 115, 22, 0.6), 0 0 40px rgba(249, 115, 22, 0.4); }
          50% { box-shadow: 0 0 30px rgba(249, 115, 22, 0.8), 0 0 60px rgba(249, 115, 22, 0.6); }
        }
        .animate-pulse-glow { animation: pulse-glow 1s ease-in-out infinite; }
      `}</style>
      
      <Confetti show={showConfetti} intensity={confettiIntensity} />
      <Header />
      
      <main className="p-3 md:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/create')} className="h-7 px-2 text-xs">
              <ChevronLeft className="w-3 h-3 mr-1" /> Back
            </Button>
            <div className="flex items-center gap-1">
              <Coins className="w-3 h-3 text-primary" />
              <span className="text-xs font-bold whitespace-nowrap">Flip - Dynamic Parlay Prediction Market</span>
            </div>
          </div>
          
          {/* LP Market Link */}
          <Card className="glass-card border-cyan-500/30 hover:border-cyan-500/60 cursor-pointer mb-3" onClick={() => navigate('/lp-flips')}>
            <CardContent className="p-3">
              <div className="flex items-center gap-3">
                <Droplets className="w-8 h-8 text-cyan-400" />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground">Liquidity Provider</p>
                  <h3 className="font-bold text-cyan-400">LP Flips Market</h3>
                </div>
                <ChevronRight className="w-5 h-5 text-cyan-400" />
              </div>
            </CardContent>
          </Card>
          
          {/* Connect Wallet */}
          {!isConnected && (
            <Card className="glass-card border-primary/30 mb-4">
              <CardContent className="p-4 flex items-center gap-4">
                <AlertCircle className="w-6 h-6 text-primary" />
                <div className="flex-1">
                  <h3 className="font-semibold">Connect Wallet to Play</h3>
                  <p className="text-xs text-muted-foreground">Connect Xverse to start</p>
                </div>
                <Button onClick={connectWallet} className="bg-primary">Connect</Button>
              </CardContent>
            </Card>
          )}
          
          {/* Player Wallet */}
          {isConnected && (
            <div className="mb-4">
              <PlayerWalletCard
                balance={playerBalance}
                onDeposit={() => setShowDepositModal(true)}
                onWithdraw={() => setShowWithdrawModal(true)}
                loading={loading}
                inPlay={stakeInPlay}
                flash={walletFlash}
              />
            </div>
          )}
          
          {/* Main Game Area */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Left: Game */}
            <Card className="glass-card">
              <CardContent className="p-4">
                {/* Setup Phase */}
                {step === 'setup' && isConnected && (
                  <div className="space-y-4">
                    <FlipSlotSelector value={numFlips} onChange={setNumFlips} disabled={loading} />
                    
                    <p className="text-sm text-center text-muted-foreground italic">
                      Pick correctly {numFlips} {numFlips === 1 ? 'time' : 'times'} in a row!
                    </p>
                    
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground">STXOSHI per flip</label>
                      <div className="flex gap-2 flex-wrap">
                        {[1, 5, 10, 50, 100].map((amount) => (
                          <button key={amount}
                            onClick={() => { setStakePerFlip(amount * 1_000_000); setCustomStake(''); }}
                            className={`flex-1 min-w-[45px] py-2 rounded-lg text-sm font-mono transition-all
                              ${stakePerFlip === amount * 1_000_000 && !customStake ? 'bg-primary text-primary-foreground' : 'bg-muted/50 hover:bg-muted'}`}
                          >
                            {amount}
                          </button>
                        ))}
                      </div>
                      <input
                        type="number"
                        placeholder="Custom amount"
                        value={customStake}
                        onChange={(e) => {
                          setCustomStake(e.target.value);
                          if (e.target.value && parseFloat(e.target.value) > 0) {
                            setStakePerFlip(parseFloat(e.target.value) * 1_000_000);
                          }
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-muted/30 border border-border text-sm font-mono"
                      />
                    </div>
                    
                    <Button
                      onClick={handleProceedToPredictions}
                      disabled={loading || playerBalance < (stakePerFlip * numFlips)}
                      className="w-full h-12 text-lg bg-gradient-to-r from-orange-500 to-orange-600"
                    >
                      {playerBalance < (stakePerFlip * numFlips) ? (
                        <><Plus className="w-4 h-4 mr-2" /> Deposit to Play</>
                      ) : (
                        <><Zap className="w-4 h-4 mr-2" /> Make Predictions</>
                      )}
                    </Button>
                    
                    {playerBalance < (stakePerFlip * numFlips) && (
                      <p className="text-center text-xs text-yellow-400">
                        Need {(stakePerFlip * numFlips) / 1_000_000} STXOSHI ({stakePerFlip / 1_000_000} × {numFlips}) to play
                      </p>
                    )}
                  </div>
                )}
                
                {/* Predictions Phase */}
                {step === 'predictions' && (
                  <div className="space-y-4">
                    <div className="text-center mb-4">
                      <h2 className="font-bold text-lg">Make Your Predictions!</h2>
                      <p className="text-sm text-muted-foreground">
                        Select HEADS or TAILS for each flip
                      </p>
                    </div>
                    
                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                      {Array.from({ length: numFlips }, (_, i) => (
                        <PredictionCard
                          key={i}
                          flipNumber={i + 1}
                          prediction={predictions[i]}
                          onSelect={(p) => handlePredictionSelect(i, p)}
                          isLocked={false}
                          isComplete={false}
                        />
                      ))}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setStep('setup')} className="flex-1">
                        Back
                      </Button>
                      <Button
                        onClick={handleStartGame}
                        disabled={!allPredictionsMade || loading}
                        className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 animate-pulse-glow"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-2" /> Start Flipping!</>}
                      </Button>
                    </div>
                    
                    {!allPredictionsMade && (
                      <p className="text-center text-xs text-yellow-400">
                        Select prediction for each flip to continue
                      </p>
                    )}
                  </div>
                )}
                
                {/* Playing Phase */}
                {step === 'playing' && (
                  <div className="space-y-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-2">
                        Flip {currentFlipIndex + 1} of {numFlips}
                      </p>
                      <p className="text-xs">
                        Your prediction: <span className="font-bold text-orange-400 uppercase">{predictions[currentFlipIndex]}</span>
                      </p>
                    </div>
                    
                    <FlipCoin3D
                      isSpinning={isSpinning}
                      finalResult={lastFlipResult?.results?.[currentFlipIndex]}
                      onAnimationEnd={handleFlipAnimationEnd}
                      initialFace={predictions[currentFlipIndex]}
                    />
                    
                    {/* Progress */}
                    <div className="flex justify-center gap-2">
                      {Array.from({ length: numFlips }, (_, i) => {
                        const result = flipResults[i];
                        const pred = predictions[i];
                        const isCorrect = result === pred;
                        const isCurrent = i === currentFlipIndex;
                        const isDone = i < flipResults.length;
                        
                        return (
                          <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2
                            ${isCurrent ? 'border-orange-500 bg-orange-500/20 text-orange-400 animate-pulse' : ''}
                            ${isDone && isCorrect ? 'border-green-500 bg-green-500/20 text-green-400' : ''}
                            ${isDone && !isCorrect ? 'border-red-500 bg-red-500/20 text-red-400' : ''}
                            ${!isCurrent && !isDone ? 'border-gray-600 bg-gray-800/30 text-gray-500' : ''}
                          `}>
                            {isDone ? (isCorrect ? '✓' : '✗') : i + 1}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Message */}
                    {currentMessage && gameState !== 'waiting' && (
                      <div className={`p-3 rounded-xl text-center ${gameState === 'won' ? 'bg-green-500/20' : gameState === 'lost' ? 'bg-red-500/20' : 'bg-orange-500/20'}`}>
                        <p className="font-bold">
                          {currentMessage.emoji} {currentMessage.text}
                        </p>
                      </div>
                    )}
                    
                    {/* Flip Button */}
                    {gameState === 'waiting' && (
                      <Button 
                        onClick={handleUserFlip} 
                        disabled={loading}
                        className="w-full h-14 text-lg animate-pulse-glow bg-gradient-to-r from-orange-500 to-orange-600"
                      >
                        {loading ? (
                          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Placing Bet...</>
                        ) : (
                          <><Zap className="w-5 h-5 mr-2" /> FLIP COIN {currentFlipIndex + 1}</>
                        )}
                      </Button>
                    )}
                    
                    {gameState === 'spinning' && (
                      <p className="text-center text-orange-400 animate-pulse font-bold">FLIPPING...</p>
                    )}
                  </div>
                )}
                
                {/* Result Phase */}
                {step === 'result' && lastFlipResult && (
                  <div className="space-y-2 text-center">
                    {lastFlipResult.won ? (
                      <div className="p-2 rounded-lg bg-green-500/20 border border-green-500 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Trophy className="w-6 h-6 text-yellow-400" />
                          <span className="font-bold text-green-400 text-sm">YOU WON! 🎉</span>
                        </div>
                        <span className="font-mono font-bold text-green-400">+{((lastFlipResult.net_profit || (lastFlipResult.payout - lastFlipResult.total_stake)) / 1_000_000).toLocaleString()} STXOSHI</span>
                      </div>
                    ) : (
                      <div className="p-2 rounded-lg bg-red-500/20 border border-red-500 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <X className="w-6 h-6 text-red-400" />
                          <span className="font-bold text-red-400 text-sm">BETTER LUCK!</span>
                        </div>
                        <span className="font-mono font-bold text-red-400">-{((stakePerFlip * numFlips) / 1_000_000).toLocaleString()} STXOSHI 😢</span>
                      </div>
                    )}
                    
                    {/* Results breakdown */}
                    <div className="space-y-2">
                      {flipResults.map((result, idx) => {
                        const pred = predictions[idx];
                        const wasCorrect = result === pred;
                        return (
                          <PredictionCard
                            key={idx}
                            flipNumber={idx + 1}
                            prediction={pred}
                            isComplete={true}
                            result={result}
                            wasCorrect={wasCorrect}
                          />
                        );
                      })}
                    </div>
                    
                    <Button onClick={handlePlayAgain} className="w-full h-12 text-lg bg-gradient-to-r from-orange-500 to-orange-600">
                      <RotateCw className="w-4 h-4 mr-2" /> PLAY AGAIN!
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Right: Info */}
            <div className="space-y-4">
              {(step === 'setup' || step === 'predictions') && (
                <Card className="glass-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" /> Payout Calculator
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PayoutDisplay stakePerFlip={stakePerFlip} numFlips={numFlips} price={price} />
                  </CardContent>
                </Card>
              )}
              
              <Card className="glass-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Coins className="w-4 h-4 text-secondary" /> Slot Liquidity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {pools.map((pool) => (
                      <div key={pool.slot} className={`flex justify-between p-2 rounded-lg text-sm
                        ${pool.slot === numFlips ? 'bg-primary/10 border border-primary' : 'bg-muted/30'}`}>
                        <span className="font-mono">{pool.slot}x ({pool.payout_multiplier}x)</span>
                        <span className="font-mono">{((pool.total_liquidity || 0) / 1_000_000).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      
      {/* Deposit Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => !loading && !checkingOnChain && setShowDepositModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <ArrowDownToLine className="w-5 h-5 text-green-400" />
              Add Funds to User Wallet
            </h3>
            <p className="text-zinc-400 text-sm mb-4">Deposit STXOSHI from your Xverse wallet</p>
            
            <div className="space-y-4">
              <input
                type="number"
                placeholder="Amount (STXOSHI)"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-muted/30 border border-border font-mono"
              />
              
              <div className="flex gap-2">
                {[10, 50, 100, 500].map(amount => (
                  <button key={amount} onClick={() => setDepositAmount(amount.toString())}
                    className="flex-1 py-2 rounded-lg bg-muted/50 hover:bg-muted text-sm font-mono">
                    {amount}
                  </button>
                ))}
              </div>
              
              <Button onClick={handleDeposit} disabled={loading || !depositAmount || parseFloat(depositAmount) < 1}
                className="w-full h-12 bg-green-600 hover:bg-green-700">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Deposit ${depositAmount || '0'} STXOSHI`}
              </Button>
              
              {/* Blue pulsating "Check On-Chain" button for stuck deposits */}
              {pendingDeposit && (
                <button
                  onClick={handleCheckOnChain}
                  disabled={checkingOnChain}
                  data-testid="check-onchain-btn"
                  className="w-full py-3 rounded-lg font-semibold text-white 
                    bg-gradient-to-r from-cyan-500 to-blue-600 
                    hover:from-cyan-400 hover:to-blue-500
                    animate-pulse shadow-lg shadow-cyan-500/30
                    disabled:opacity-50 disabled:animate-none
                    flex items-center justify-center gap-2"
                >
                  {checkingOnChain ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Checking blockchain...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Check On-Chain (if stuck on this page)
                    </>
                  )}
                </button>
              )}
              
              <Button variant="ghost" onClick={() => setShowDepositModal(false)} disabled={loading || checkingOnChain} className="w-full">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => !loading && setShowWithdrawModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <ArrowUpFromLine className="w-5 h-5 text-orange-400" />
              Withdraw from User Wallet
            </h3>
            
            <div className="p-4 rounded-xl bg-muted/30 border border-border mb-4">
              <p className="text-sm text-muted-foreground">Available Balance</p>
              <p className="text-2xl font-mono font-bold text-green-400">
                {(playerBalance / 1_000_000).toLocaleString()} STXOSHI
              </p>
            </div>
            
            <Button onClick={handleWithdraw} disabled={loading || playerBalance <= 0}
              className="w-full h-12 bg-orange-600 hover:bg-orange-700 mb-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Withdraw All'}
            </Button>
            
            <Button variant="ghost" onClick={() => setShowWithdrawModal(false)} disabled={loading} className="w-full">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
